<template>
  <div v-if="loading" class="text-center font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" />
  </div>

  <div v-else class="animated fadeIn">
    <b-row class="mb-3">
      <b-col>
        <b-input-group prepend="Analyte">
          <b-form-select
            v-model="analyte_value"
            :options="analyte_options"
          >
            <template #first>
              <option :value="null">
                Select Analyte
              </option>
            </template>
          </b-form-select>
        </b-input-group>
      </b-col>

      <b-col>
        <b-input-group prepend="Crop Type">
          <b-form-select
            v-model="crop_type_value"
            :options="cropOptions"
          >
            <template #first>
              <option :value="null">
                Select Crop Type
              </option>
            </template>
          </b-form-select>
        </b-input-group>
      </b-col>

      <b-col>
        <b-input-group prepend="Crop Stage">
          <b-form-select
            v-model="crop_stage_value"
            :options="cropStageOptions"
          >
            <template #first>
              <option :value="null">
                Select Crop Stage
              </option>
            </template>
          </b-form-select>
        </b-input-group>
      </b-col>
    </b-row>

    <b-card no-body>
      <b-overlay :show="loading_analyte_ranges" class="animated">
        <b-card-body>
          <b-row class="mb-3 font-weight-bolder">
            <b-col>Analyte</b-col>
            <b-col>Crop Type</b-col>
            <b-col>Crop Stage</b-col>
            <b-col>Low</b-col>
            <b-col>High</b-col>
            <b-col md="1" />
          </b-row>

          <template v-if="analyte_ranges && analyte_ranges.length">
            <AnalyteRangeFormRow
              v-for="(item, index) in analyte_ranges"
              :key="'range' + index"
              :index="index"
              :item="item"
              :fields="[
                'analyte_id',
                'attribute_list_id',
                'sub_attribute_list_id',
                'low',
                'high'
              ]"
              class="py-2 align-items-center"
              @refresh="fetchAnalyteRanges"
            >
              <template #field(analyte_id)="{ row }">
                {{ row.analyte.name }}
              </template>

              <template #field(attribute_list_id)="{ row }">
                {{ row.attribute_list ? row.attribute_list.value : '' }}
              </template>

              <template #field(sub_attribute_list_id)="{ row }">
                {{ row.sub_attribute_list ? row.sub_attribute_list.value : '' }}
              </template>

              <template #field(low)="{ row }">
                <b-form-input
                  v-model="row.low"
                  type="text"
                  placeholder="Low"
                />
              </template>

              <template #field(high)="{ row }">
                <b-form-input
                  v-model="row.high"
                  type="text"
                  placeholder="High"
                />
              </template>
            </AnalyteRangeFormRow>

            <b-pagination
              v-model="current_page"
              :total-rows="total_items"
              :per-page="items_per_page"
              class="mt-3 mb-1"
            />
            Showing {{ (current_page - 1) * items_per_page + 1 }} to {{ Math.min(current_page * items_per_page, total_items) }} of {{ total_items }} entries
          </template>

          <b-row v-else class="py-2 align-items-center bg-light">
            <b-col>
              <div class="text-center text-muted">
                No Analyte Ranges Found
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-overlay>
    </b-card>

    <b-card v-if="addingNew" no-body>
      <b-card-header>
        <span class="text-muted text-uppercase">Add Analyte Range</span>
      </b-card-header>

      <b-card-body>
        <b-row class="mb-1 py-2 font-weight-bolder bg-light">
          <b-col>Analyte</b-col>
          <b-col>Crop Type</b-col>
          <b-col>Crop Stage</b-col>
          <b-col>Low</b-col>
          <b-col>High</b-col>
        </b-row>

        <SampleTypeAnalyteRangeAddForm
          :sample_type="sample_type"
          :fields="[
            'analyte_id',
            'attribute_list_id',
            'sub_attribute_list_id',
            'low',
            'high'
          ]"
          @close="addingNew = false"
          @saved="fetchAnalyteRanges"
        >
          <template #field(analyte_id)="{ row }">
            <b-form-select
              v-model="row.analyte_id"
              :options="analyte_options"
            >
              <template #first>
                <option :value="null">
                  Select Analyte
                </option>
              </template>
            </b-form-select>
          </template>

          <template #field(attribute_list_id)="{ row }">
            <b-form-select
              v-model="row.attribute_list_id"
              :options="cropOptions"
            >
              <template #first>
                <option :value="null">
                  Select Crop Type
                </option>
              </template>
            </b-form-select>
          </template>

          <template #field(sub_attribute_list_id)="{ row }">
            <b-form-select
              v-model="row.sub_attribute_list_id"
              :options="cropStageOptions"
            >
              <template #first>
                <option :value="null">
                  Select Crop Stage
                </option>
              </template>
            </b-form-select>
          </template>

          <template #field(low)="{ row }">
            <b-form-input
              v-model="row.low"
              type="text"
              placeholder="Low"
            />
          </template>

          <template #field(high)="{ row }">
            <b-form-input
              v-model="row.high"
              type="text"
              placeholder="High"
            />
          </template>
        </SampleTypeAnalyteRangeAddForm>
      </b-card-body>
    </b-card>

    <b-button v-else block variant="primary" @click="addingNew = true" class="mb-5">
      <i class="fa fa-plus" /> Add Analyte Range
    </b-button>
  </div>
</template>

<script>
import { getCollection } from '@/shared/fetch-utils';
import AnalyteRangeFormRow from '../../analyte/form/fields/AnalyteRangeFormRow.vue';
import SampleTypeAnalyteRangeAddForm from './SampleTypeAnalyteRangeAddForm.vue';

export default {
  name: 'SampleTypeAnalyteRangeFormTissue',
  components: {
    AnalyteRangeFormRow,
    SampleTypeAnalyteRangeAddForm
  },
  props: {
    sample_type: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      loading: true,
      loading_analyte_ranges: true,

      // Analytes
      analyte_collection: null,
      analyte_options: null,
      analyte_value: null,

      // Pagination
      current_page: 1,
      items_per_page: 10,
      total_items: 0,

      managed_list_collection: null,

      crop_type_value: null,
      crop_stage_value: null,

      analyte_range_collection: null,
      analyte_ranges: [],

      addingNew: false
    }
  },
  computed: {
    managedLists () {
      return this.managed_list_collection &&
        this.managed_list_collection.models &&
        this.managed_list_collection.models.length
        ? this.managed_list_collection.models
        : []
    },

    cropTypes () {
      return this.managedLists.find(
        item => item.sub_type === 'tissue' && item.list === 'type'
      ).attribute_lists
    },

    cropOptions () {
      return this.cropTypes.map((attribute) => {
        return {
          value: attribute.id,
          text: attribute.value
        }
      })
    },

    cropStages () {
      return this.managedLists.find(
        item => item.sub_type === 'tissue' && item.list === 'growth_stage'
      ).attribute_lists
    },

    cropStageOptions () {
      return this.cropStages.map((attribute) => {
        return {
          value: attribute.id,
          text: attribute.value
        }
      })
    }
  },
  watch: {
    analyte_value () {
      this.crop_type_value = null
      this.crop_stage_value = null
      this.current_page = 1
      this.total_items = 0
      this.fetchAnalyteRanges()
    },
    crop_type_value () {
      if (!this.loading_analyte_ranges) {
        this.current_page = 1
        this.total_items = 0
        this.fetchAnalyteRanges()
      }
    },
    crop_stage_value () {
      if (!this.loading_analyte_ranges) {
        this.current_page = 1
        this.total_items = 0
        this.fetchAnalyteRanges()
      }
    },
    current_page () {
      this.fetchAnalyteRanges()
    }
  },
  async created () {
    this.loading = true

    await this.fetchAnalytes()

    this.managed_list_collection = await getCollection({
      collection_type: 'managed_list',
      message: this.$bvToast,
      limit: 'all'
    })

    this.fetchAnalyteRanges()

    this.loading = false
  },
  methods: {
    async fetchAnalytes () {
      this.analyte_collection = await getCollection({
        collection_type: 'analyte',
        message: this.$bvToast,
        filters: [
          {
            logic: 'filterEquals',
            field: 'sample_type_id',
            value: this.sample_type.id
          }
        ],
        limit: 'all'
      })

      this.analyte_options = this.analyte_collection.map((analyte) => {
        return {
          value: analyte.id,
          text: analyte.name
        }
      })
    },

    async fetchAnalyteRanges () {
      this.loading_analyte_ranges = true

      const filters = [
        {
          logic: 'filterEquals',
          field: 'analyte.sample_type_id',
          value: this.sample_type.id
        }
      ]

      if (this.analyte_value) {
        filters.push({
          logic: 'filterEquals',
          field: 'analyte_id',
          value: this.analyte_value
        })
      }

      if (this.crop_type_value) {
        filters.push({
          logic: 'filterEquals',
          field: 'attribute_list_id',
          value: this.crop_type_value
        })
      }

      if (this.crop_stage_value) {
        filters.push({
          logic: 'filterEquals',
          field: 'sub_attribute_list_id',
          value: this.crop_stage_value
        })
      }

      this.analyte_range_collection = await getCollection({
        collection_type: 'analyte_range',
        message: this.$bvToast,
        filters,
        limit: this.items_per_page,
        page: this.current_page
      })

      this.total_items = this.analyte_range_collection.get('count')

      this.analyte_ranges = this.analyte_range_collection.map((analyte_range) => {
        return {
          id: analyte_range.id,
          analyte_id: analyte_range.analyte_id,
          analyte: analyte_range.analyte,
          attribute_list_id: analyte_range.attribute_list_id,
          attribute_list: analyte_range.attribute_list,
          sub_attribute_list_id: analyte_range.sub_attribute_list_id,
          sub_attribute_list: analyte_range.sub_attribute_list,
          low: analyte_range.low,
          high: analyte_range.high
        }
      })

      this.loading_analyte_ranges = false
    }
  }
}
</script>
